import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Button, Container, Form, Table, Card, Row, Col, Modal } from 'react-bootstrap';
import AgreementNameField from '../components/AgreementNameField';
import { cleanupModalBody } from '../utils/modalCleanup';

function formatType(type) {
  if (type === 'P') return 'Private';
  if (type === 'S') return 'Shared';
  return type || '—';
}

const SHARED_AGREEMENT_TYPE = 'S';

function emptyForm() {
  return {
    name: '',
    hire_disc_pct: '',
    sales_disc_pct: '',
    start_date: '',
    end_date: '',
  };
}

function toForm(record) {
  if (!record) return emptyForm();
  return {
    name: record.name ?? '',
    hire_disc_pct: record.hire_disc_pct ?? '',
    sales_disc_pct: record.sales_disc_pct ?? '',
    start_date: record.start_date ? String(record.start_date).slice(0, 10) : '',
    end_date: record.end_date ? String(record.end_date).slice(0, 10) : '',
  };
}

function RecordModal({ show, record, onClose, onSave, saving }) {
  const [formData, setFormData] = useState(emptyForm());
  const [modalError, setModalError] = useState('');

  useEffect(() => {
    if (!show) {
      return;
    }
    setFormData(toForm(record));
    setModalError('');
  }, [record, show]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name?.trim()) {
      setModalError('Name is required');
      return;
    }

    try {
      await onSave({
        type: record ? record.type : SHARED_AGREEMENT_TYPE,
        name: formData.name.trim(),
        hire_disc_pct: formData.hire_disc_pct === '' ? null : formData.hire_disc_pct,
        sales_disc_pct: formData.sales_disc_pct === '' ? null : formData.sales_disc_pct,
        start_date: formData.start_date || null,
        end_date: formData.end_date || null,
      });
    } catch (err) {
      setModalError(err.response?.data?.error || 'Failed to save record');
    }
  };

  return (
    <Modal
      show={show}
      onHide={onClose}
      onExited={cleanupModalBody}
      centered
      enforceFocus={false}
      restoreFocus={false}
      style={{ zIndex: 1060 }}
    >
      <Modal.Header closeButton>
        <Modal.Title>{record ? 'Edit Pricing Agreement' : 'Add Pricing Agreement'}</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {modalError && <div className="alert alert-danger">{modalError}</div>}

          <AgreementNameField
            label="Name"
            inputName="name"
            required
            active={show}
            refreshKey={record?.id ?? 'new'}
            value={formData.name}
            onChange={(name) => setFormData((p) => ({ ...p, name }))}
          />

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Hire Disc Pct</Form.Label>
                <Form.Control
                  type="number"
                  name="hire_disc_pct"
                  min={1}
                  max={99}
                  step="0.01"
                  value={formData.hire_disc_pct}
                  onChange={(e) => setFormData((p) => ({ ...p, hire_disc_pct: e.target.value }))}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Sales Disc Pct</Form.Label>
                <Form.Control
                  type="number"
                  name="sales_disc_pct"
                  min={1}
                  max={99}
                  step="0.01"
                  value={formData.sales_disc_pct}
                  onChange={(e) => setFormData((p) => ({ ...p, sales_disc_pct: e.target.value }))}
                />
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Start Date</Form.Label>
                <Form.Control
                  type="date"
                  name="start_date"
                  value={formData.start_date}
                  onChange={(e) => setFormData((p) => ({ ...p, start_date: e.target.value }))}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>End Date</Form.Label>
                <Form.Control
                  type="date"
                  name="end_date"
                  value={formData.end_date}
                  onChange={(e) => setFormData((p) => ({ ...p, end_date: e.target.value }))}
                />
              </Form.Group>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button type="submit" disabled={saving}>
            {saving ? 'Saving...' : record ? 'Update' : 'Create'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

function PricingAgreements() {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const fetchGeneration = useRef(0);

  const fetchRecords = async (search = '', { silent = false } = {}) => {
    const generation = ++fetchGeneration.current;
    try {
      if (!silent) {
        setLoading(true);
      }
      const response = await axios.get('/api/pricing-agreements', {
        params: { page: 1, limit: 10000, search },
      });
      if (generation !== fetchGeneration.current) {
        return;
      }
      setRecords(response.data.pricingAgreements || []);
    } catch {
      if (generation === fetchGeneration.current) {
        setError('Failed to fetch pricing agreements');
      }
    } finally {
      if (!silent && generation === fetchGeneration.current) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchRecords();
  }, []);

  const handleSave = async (payload) => {
    setSaving(true);
    try {
      fetchGeneration.current += 1;
      if (editing) {
        const response = await axios.put(`/api/pricing-agreements/${editing.id}`, payload);
        setRecords((prev) => prev.map((row) => (row.id === editing.id ? response.data : row)));
        setSuccess('Pricing agreement updated successfully');
      } else {
        const response = await axios.post('/api/pricing-agreements', payload);
        setRecords((prev) =>
          [...prev, response.data].sort((a, b) => String(a.name).localeCompare(String(b.name)))
        );
        setSuccess('Pricing agreement added successfully');
      }
      setSearchTerm('');
      setShowModal(false);
      setEditing(null);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this pricing agreement?')) return;
    try {
      fetchGeneration.current += 1;
      await axios.delete(`/api/pricing-agreements/${id}`);
      setRecords((prev) => prev.filter((row) => row.id !== id));
      setSuccess('Pricing agreement deleted successfully');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete pricing agreement');
    }
  };

  const handleDeleteAll = async () => {
    if (
      !window.confirm(
        'Are you sure you want to delete ALL pricing agreements? This action cannot be undone!'
      )
    ) {
      return;
    }
    if (
      !window.confirm(
        'This will permanently delete all your pricing agreement records. Are you absolutely sure?'
      )
    ) {
      return;
    }
    try {
      fetchGeneration.current += 1;
      const response = await axios.delete('/api/pricing-agreements');
      setRecords([]);
      setSuccess(
        `All pricing agreements deleted successfully (${response.data.deletedCount || 0} records)`
      );
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete all pricing agreements');
    }
  };

  return (
    <Container fluid>
      <Row className="mt-0 mb-2">
        <Col>
          <h1 className="mb-0">Pricing Agreements</h1>
          <p className="text-muted mb-0">Manage pricing agreement headers</p>
        </Col>
        <Col xs="auto" className="d-flex gap-2">
          <Button
            onClick={() => {
              setEditing(null);
              setShowModal(true);
            }}
          >
            Add Agreement
          </Button>
          <Button variant="danger" onClick={handleDeleteAll} disabled={records.length === 0}>
            Delete All
          </Button>
        </Col>
      </Row>

      {error && <div className="alert alert-danger">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      <Card>
        <div className="card-header">
          <Form
            className="d-flex gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              fetchRecords(searchTerm);
            }}
          >
            <Form.Control
              placeholder="Search name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ maxWidth: 250 }}
            />
            <Button type="submit" size="sm">
              Search
            </Button>
          </Form>
        </div>
        <div className="card-body p-0">
          {loading ? (
            <div className="p-4">Loading...</div>
          ) : (
            <Table hover responsive className="mb-0">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Name</th>
                  <th>Hire Disc %</th>
                  <th>Sales Disc %</th>
                  <th>Start</th>
                  <th>End</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {records.map((row) => (
                  <tr key={row.id}>
                    <td>{formatType(row.type)}</td>
                    <td>{row.name}</td>
                    <td>{row.hire_disc_pct ?? '—'}</td>
                    <td>{row.sales_disc_pct ?? '—'}</td>
                    <td>{row.start_date ? String(row.start_date).slice(0, 10) : '—'}</td>
                    <td>{row.end_date ? String(row.end_date).slice(0, 10) : '—'}</td>
                    <td>
                      <Button
                        size="sm"
                        variant="outline-primary"
                        className="me-1"
                        onClick={() => {
                          setEditing(row);
                          setShowModal(true);
                        }}
                      >
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        variant="outline-danger"
                        onClick={() => handleDelete(row.id)}
                      >
                        Delete
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </div>
      </Card>

      <RecordModal
        show={showModal}
        record={editing}
        saving={saving}
        onClose={() => {
          if (saving) return;
          setShowModal(false);
          setEditing(null);
        }}
        onSave={handleSave}
      />
    </Container>
  );
}

export default PricingAgreements;
